// src/pages/DraftWars/draftWarsHooks.js
// Shared hooks + server-time sync for Draft Wars.
// Fixes clock-skew bugs where different clients see different countdowns.

import { useEffect, useState } from "react";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

// ─── Server-time offset (module-scoped so it's shared across hooks) ─────────
// offset = serverMillis - clientMillis   (so to convert: serverNow = Date.now() + offset)
let cachedOffset = 0;
let lastSyncedAt = 0;
let syncInFlight = null;

// Probe: write a serverTimestamp to a dedicated doc, read it back, compute offset.
// Uses best-effort latency halving (the round-trip is split into two equal halves).
async function probeServerTime() {
  const probeRef = doc(db, "draftWars", "_clockProbe");
  const t0 = Date.now();
  await setDoc(probeRef, { ts: serverTimestamp() });
  const snap = await getDoc(probeRef);
  const t1 = Date.now();
  const serverMs = snap.data()?.ts?.toMillis?.();
  if (!serverMs) throw new Error("clock probe failed");
  const midClient = (t0 + t1) / 2;
  return serverMs - midClient;
}

/**
 * Force a server-time sync. Safe to call many times — only one probe runs at once,
 * and subsequent calls within 60s return the cached offset.
 */
export async function syncServerTime() {
  if (syncInFlight) return syncInFlight;
  if (Date.now() - lastSyncedAt < 60_000) return cachedOffset;
  syncInFlight = probeServerTime()
    .then((offset) => {
      cachedOffset = offset;
      lastSyncedAt = Date.now();
      return offset;
    })
    .catch(() => cachedOffset)
    .finally(() => { syncInFlight = null; });
  return syncInFlight;
}

/** Current time in the server's clock (client local + offset). */
export function serverNow() {
  return Date.now() + cachedOffset;
}

/** Call once on mount of any Draft Wars page to keep the client in sync with server time. */
export function useServerTimeSync() {
  useEffect(() => {
    syncServerTime();
    // Re-sync every 5 min in case of clock drift or network changes
    const id = setInterval(() => syncServerTime(), 5 * 60_000);
    return () => clearInterval(id);
  }, []);
}

/**
 * Live countdown in seconds until `timerEndAt` (a Firestore Timestamp or ms).
 * Returns null if no timer is set.
 *
 * - Uses `serverNow()` so the countdown is server-aware (fixes clock skew)
 * - Reads Date.now() at render time (not cached) so refreshing doesn't show stale values
 */
export function useCountdown(timerEndAt) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!timerEndAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 100);
    return () => clearInterval(id);
  }, [timerEndAt]);

  if (!timerEndAt) return null;
  const endMs =
    typeof timerEndAt.toMillis === "function" ? timerEndAt.toMillis() : timerEndAt;
  return Math.max(0, Math.ceil((endMs - serverNow()) / 1000));
}
